import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Newspaper, 
  Award, 
  FileText, 
  Briefcase, 
  Receipt,
  Users, 
  LogOut, 
  Home,
  Menu,
  X,
  Building,
  ShieldCheck,
  FileArchive,
  UserCircle,
  GraduationCap,
  Stethoscope,
  Landmark,
  Shield,
  Vote,
  Store,
  UserPlus,
  LayoutDashboard,
  MessageSquare,
  Building2,
  IdCard,
  ChevronRight,
  Sparkles,
  Ban,
  ShoppingCart,
  Flag,
  History
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Layout = ({ children }) => {
  const { 
    user, 
    logout, 
    isSindacatoAdmin,
    canViewScontrini, 
    canManageDipendenti,
    canViewDocumenti,
    hasAzienda,
    canAccessScuola,
    canAccessOspedale,
    canAccessBanca,
    canAccessMulte,
    canAccessSeggi,
    hasPermesso
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Verifica se l'utente è bannato
  const isBanned = user?.ban_info && (user.ban_info.permanente || new Date(user.ban_info.data_scadenza) > new Date());

  // Se bannato, reindirizza al forum se non è già lì
  useEffect(() => {
    if (isBanned && !location.pathname.startsWith('/forum')) {
      navigate('/forum');
    }
  }, [isBanned, location.pathname, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Menu dinamico basato su permessi
  const getNavItems = () => {
    // Se l'utente è bannato, mostra solo il forum
    if (isBanned) {
      return [
        { to: '/forum', icon: MessageSquare, label: 'Forum', show: true },
      ];
    }

    const items = [
      { to: '/dashboard', icon: Home, label: 'Dashboard', show: true },
      { to: '/profilo', icon: UserCircle, label: 'Il Mio Profilo', show: true },
    ];

    // GESTIONE AZIENDE - Hub centrale per direttori
    // Contiene: Scontrini, Dipendenti, Assunzioni, Concorsi, Domande Lavoro
    if (hasAzienda() || isSindacatoAdmin()) {
      items.push({ to: '/gestione-aziende', icon: Building2, label: 'Gestione Aziende', show: true });
    }

    // Sezioni pubbliche
    items.push({ to: '/gazzetta', icon: Newspaper, label: 'Gazzetta', show: true });
    items.push({ to: '/concorsi', icon: Award, label: 'Concorsi', show: true });
    items.push({ to: '/curriculum', icon: FileText, label: 'Curriculum', show: true });
    items.push({ to: '/ldl', icon: Briefcase, label: 'Domande Lavoro', show: true });
    items.push({ to: '/patenti', icon: IdCard, label: 'Patenti', show: true });

    // Documenti: richiede permesso o accesso Municipio
    if (canViewDocumenti()) {
      items.push({ to: '/documenti', icon: FileArchive, label: 'Documenti', show: true });
    }

    // Moduli Aziendali specifici (Scuola, Ospedale, etc. hanno logiche proprie)
    if (canAccessScuola()) {
      items.push({ to: '/scuola', icon: GraduationCap, label: 'Scuola', show: true });
    }

    if (canAccessOspedale()) {
      items.push({ to: '/ospedale', icon: Stethoscope, label: 'Ospedale', show: true });
    }

    if (canAccessBanca()) {
      items.push({ to: '/banca', icon: Landmark, label: 'Banca', show: true });
      // Partite IVA - solo per chi può gestire conti o admin
      if (isSindacatoAdmin() || hasPermesso('gestisci_conti')) {
        items.push({ to: '/partite-iva', icon: Briefcase, label: 'Partite IVA', show: true });
      }
    }

    if (canAccessMulte()) {
      items.push({ to: '/multe', icon: Shield, label: 'Multe', show: true });
    }

    if (canAccessSeggi()) {
      items.push({ to: '/seggi', icon: Vote, label: 'Seggi Elettorali', show: true });
    }

    // Negozio - visibile a tutti
    items.push({ to: '/negozio', icon: Store, label: 'Negozio', show: true });

    // Forum - visibile a tutti
    items.push({ to: '/forum', icon: MessageSquare, label: 'Forum', show: true });

    // Store Roleplay - visibile a tutti
    items.push({ to: '/store', icon: ShoppingCart, label: 'Store Roleplay', show: true });

    // Partiti Politici - visibile a tutti
    items.push({ to: '/partiti', icon: Flag, label: 'Partiti Politici', show: true });

    // --- SEZIONE ADMIN/STAFF ---
    
    // Gestione Ban - per chi ha il permesso gestisci_ban o è admin
    if (isSindacatoAdmin() || hasPermesso('gestisci_ban')) {
      items.push({ to: '/gestione-ban', icon: Ban, label: 'Gestione Ban', show: true });
    }
    
    // Dashboard Admin - per Admin o chi ha permesso visualizza_dashboard_admin
    if (isSindacatoAdmin() || hasPermesso('visualizza_dashboard_admin')) {
      items.push({ to: '/admin-dashboard', icon: LayoutDashboard, label: 'Dashboard Admin', show: true });
    }
    
    // Solo Admin Sindacato
    if (isSindacatoAdmin()) {
      items.push({ to: '/aziende', icon: Building, label: 'Crea Aziende', show: true });
      items.push({ to: '/utenti', icon: Users, label: 'Gestione Utenti', show: true });
      items.push({ to: '/ruoli-azienda', icon: ShieldCheck, label: 'Ruoli e Permessi', show: true });
      items.push({ to: '/admin-log', icon: History, label: 'Log Attività', show: true });
    }

    return items.filter(item => item.show);
  };

  const navItems = getNavItems();

  // Mostra ruolo in modo leggibile
  const getRuoloDisplay = () => {
    if (user?.ruolo === 'SINDACATO_ADMIN') return 'Admin Sindacato';
    if (user?.ruolo === 'STAFF') return user?.ruolo_azienda_nome || 'Staff';
    return user?.ruolo || '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl transition-all duration-300"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar - Futuristic Design */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 transform transition-all duration-300 ease-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-500/10 to-transparent rounded-full blur-2xl" />
          <div className="absolute bottom-1/3 left-0 w-24 h-24 bg-gradient-to-tr from-violet-500/10 to-transparent rounded-full blur-2xl" />
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-blue-500/5 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="flex flex-col h-full relative z-10">
          {/* Header */}
          <div className="p-5 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-violet-500 rounded-2xl blur-md opacity-50" />
                <img 
                  src="/nova-logo.png" 
                  alt="Nova Roleplay" 
                  className="relative w-12 h-12 rounded-2xl object-cover ring-2 ring-white/10"
                />
              </div>
              <div>
                <h1 className="font-bold text-lg text-white tracking-tight">Nova Roleplay</h1>
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-cyan-400" />
                  <p className="text-xs text-cyan-400 font-medium">Gestionale Pro</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
            {navItems.map((item, index) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace('/', '')}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-gradient-to-r from-cyan-500/20 to-violet-500/20 text-white shadow-lg shadow-cyan-500/10 border border-white/10' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <div className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 group-hover:scale-110`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <span className="flex-1">{item.label}</span>
                <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0 transition-all duration-200" />
              </NavLink>
            ))}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 backdrop-blur-sm mb-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20">
                  <span className="text-sm font-bold text-white">
                    {user?.nome?.[0]}{user?.cognome?.[0]}
                  </span>
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-900" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                  {user?.nome} {user?.cognome}
                </p>
                <p className="text-xs text-slate-400">
                  {getRuoloDisplay()}
                </p>
                {user?.azienda_nome && (
                  <p className="text-xs text-cyan-400 font-medium truncate">{user.azienda_nome}</p>
                )}
              </div>
            </div>
            <Button
              data-testid="logout-button"
              variant="ghost"
              className="w-full justify-start gap-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
              Esci
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden" 
          onClick={() => setSidebarOpen(false)} 
        />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Banner Ban - mostra avviso se l'utente è bannato */}
        {isBanned && (
          <div className="bg-red-600 text-white px-4 py-3">
            <div className="flex items-center justify-center gap-3">
              <Ban className="w-5 h-5" />
              <div className="text-center">
                <p className="font-semibold">Account Sospeso</p>
                <p className="text-sm text-red-100">
                  Sei stato bannato da @{user?.ban_info?.banned_by_username || user?.ban_info?.banned_by_nome || 'Staff'}.
                  {user?.ban_info?.motivo && (
                    <span className="block mt-1 font-medium">Motivo: {user.ban_info.motivo}</span>
                  )}
                  Puoi accedere solo al Forum.
                  {user?.ban_info?.data_scadenza && !user?.ban_info?.permanente && (
                    <span className="block mt-1">Scadenza: {new Date(user.ban_info.data_scadenza).toLocaleDateString('it-IT')}</span>
                  )}
                  {user?.ban_info?.permanente && (
                    <span className="block mt-1 text-red-200 font-medium">Ban Permanente</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
